// ./src/components/generic/FilePickerDrawer/FilePickerDrawer.tsx
import React, { useState, useCallback, useMemo, useEffect } from "react";
import {
  Stack,
  TextInput,
  Group,
  Text,
  Box,
  SimpleGrid,
  SegmentedControl,
  Divider,
  Button,
  Center,
  Loader,
  ScrollArea,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useTranslation } from "react-i18next";

import { makeUniqueFilename } from "@/utils/nameHelpers";
import { UploadedFile, UploadedFileFilter } from "@/utils/api/UploadedFileService";

import { useUploadedFiles } from "@/hooks/useUploadedFiles";

import { AppDrawer } from "../AppDrawer";
import { AppIcon } from "../AppIcon";
import { FileUpload } from "../FileUpload";
import { SelectableFileCard } from "../SelectableFileCard";
import { SortConfig } from "../AppTable";

// ============================================
// Types
// ============================================

export interface FilePickerDrawerProps {
  /** Whether the drawer is open */
  opened: boolean;

  /** Callback to close the drawer */
  onClose: () => void;

  /** Callback when files are confirmed */
  onConfirm: (selectedFiles: UploadedFile[]) => void;

  /** File IDs to exclude (already attached) - shown as disabled */
  excludeFileIds?: string[];

  /** Whether to allow multiple file selection */
  multiple?: boolean;

  /** Accepted file types for upload (MIME types or extensions) */
  accept?: string;

  /** Maximum file size for upload in bytes */
  maxFileSize?: number;

  /** Custom drawer title */
  title?: string;

  /** Drawer size */
  size?: "sm" | "md" | "lg" | "xl";
}

// ============================================
// Types for sorting
// ============================================

type SortOption = "name-asc" | "name-desc" | "date-asc" | "date-desc";

const SORT_OPTIONS: Record<SortOption, SortConfig> = {
  "name-asc": { key: "name", direction: "asc" },
  "name-desc": { key: "name", direction: "desc" },
  "date-asc": { key: "createdAt", direction: "asc" },
  "date-desc": { key: "createdAt", direction: "desc" },
};

// ============================================
// Component
// ============================================

export const FilePickerDrawer: React.FC<FilePickerDrawerProps> = ({
  opened,
  onClose,
  onConfirm,
  excludeFileIds = [],
  multiple = true,
  accept = "*",
  maxFileSize = 10 * 1024 * 1024, // 10MB default
  title,
  size = "lg",
}) => {
  const { t } = useTranslation(["common"]);

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch] = useDebouncedValue(searchQuery, 300);

  // Sort state
  const [sortOption, setSortOption] = useState<SortOption>("date-desc");

  // Selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Upload state
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);

  // Use uploaded files hook
  const {
    uploadedFiles,
    loading,
    loadingMore,
    hasMore,
    list,
    create,
    loadMore,
    isCreating,
  } = useUploadedFiles({
    pageSize: 20,
    autoLoad: false,
  });

  // Excluded file IDs as a Set for faster lookup
  const excludedIdsSet = useMemo(
    () => new Set(excludeFileIds),
    [excludeFileIds]
  );

  // Get selected files (full objects)
  const selectedFiles = useMemo(() => {
    return uploadedFiles.filter((file) => file.id && selectedIds.has(file.id));
  }, [uploadedFiles, selectedIds]);

  // Build filter based on search
  const buildFilter = useCallback((): Partial<UploadedFileFilter> => {
    const filter: Partial<UploadedFileFilter> = {};

    if (debouncedSearch.trim()) {
      filter.nameLike = debouncedSearch.trim();
    }

    return filter;
  }, [debouncedSearch]);

  // Load files when drawer opens or search/sort changes
  useEffect(() => {
    if (opened) {
      const filter = buildFilter();
      const sortConfig = SORT_OPTIONS[sortOption];
      list(filter, { page: 1, pageSize: 20 }, [sortConfig], true);
    }
  }, [opened, debouncedSearch, sortOption, list, buildFilter]);

  // Reset state when drawer opens
  useEffect(() => {
    if (opened) {
      setSelectedIds(new Set());
      setSearchQuery("");
      setUploadError(null);
      setUploadSuccess(null);
    }
  }, [opened]);

  // Handle file selection
  const handleSelect = useCallback(
    (fileId: string, selected: boolean) => {
      setSelectedIds((prev) => {
        const newSet = new Set(prev);

        if (selected) {
          if (multiple) {
            newSet.add(fileId);
          } else {
            // Single selection mode - replace
            newSet.clear();
            newSet.add(fileId);
          }
        } else {
          newSet.delete(fileId);
        }

        return newSet;
      });
    },
    [multiple]
  );

  // Handle file upload
  const handleFilesSelected = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;

      setIsUploading(true);
      setUploadError(null);
      setUploadSuccess(null);

      try {
        // Upload the first file (FileUpload component handles single file)
        const file = files[0];
        const uniqueName = makeUniqueFilename(file.name);
        const result = await create({
          files: [file],
          name: uniqueName,
          originalFilename: file.name,
        });

        if (result) {
          setUploadSuccess(
            t("common:attachments.uploadSuccess", {
              filename: file.name,
              defaultValue: `"${file.name}" uploaded successfully`,
            })
          );

          // Auto-select the uploaded file
          if (result.id) {
            setSelectedIds((prev) => {
              const newSet = new Set(prev);
              if (multiple) {
                newSet.add(result.id!);
              } else {
                newSet.clear();
                newSet.add(result.id!);
              }
              return newSet;
            });
          }

          // Clear success message after 3 seconds
          setTimeout(() => setUploadSuccess(null), 3000);
        } else {
          setUploadError(
            t("common:attachments.uploadFailed", {
              defaultValue: "Failed to upload file. Please try again.",
            })
          );
        }
      } catch (error) {
        setUploadError(
          t("common:attachments.uploadFailed", {
            defaultValue: "Failed to upload file. Please try again.",
          })
        );
      } finally {
        setIsUploading(false);
      }
    },
    [create, makeUniqueFilename, multiple, t]
  );

  // Handle confirm
  const handleConfirm = useCallback(() => {
    onConfirm(selectedFiles);
  }, [onConfirm, selectedFiles]);

  // Handle load more
  const handleLoadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      loadMore();
    }
  }, [loadMore, loadingMore, hasMore]);

  // Handle search change
  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(event.target.value);
    },
    []
  );

  // Handle sort change
  const handleSortChange = useCallback((value: string) => {
    setSortOption(value as SortOption);
  }, []);

  // Sort options for segmented control
  const sortSegmentData = useMemo(
    () => [
      {
        value: "date-desc",
        label: (
          <Center style={{ gap: 4 }}>
            <AppIcon iconName="Calendar" size={14} />
            <Text size="xs">↓</Text>
          </Center>
        ),
      },
      {
        value: "date-asc",
        label: (
          <Center style={{ gap: 4 }}>
            <AppIcon iconName="Calendar" size={14} />
            <Text size="xs">↑</Text>
          </Center>
        ),
      },
      {
        value: "name-asc",
        label: (
          <Center style={{ gap: 4 }}>
            <AppIcon iconName="SortAscending" size={14} />
            <Text size="xs">A-Z</Text>
          </Center>
        ),
      },
      {
        value: "name-desc",
        label: (
          <Center style={{ gap: 4 }}>
            <AppIcon iconName="SortDescending" size={14} />
            <Text size="xs">Z-A</Text>
          </Center>
        ),
      },
    ],
    []
  );

  // Render file grid
  const renderFileGrid = () => {
    if (loading && uploadedFiles.length === 0) {
      return (
        <Center py="xl">
          <Loader size="md" />
        </Center>
      );
    }

    if (uploadedFiles.length === 0) {
      return (
        <Center py="xl">
          <Box ta="center">
            <AppIcon
              iconName="FolderOpen"
              size={48}
              style={{ color: "var(--mantine-color-gray-4)" }}
            />
            <Text size="sm" c="dimmed" mt="md">
              {debouncedSearch
                ? t("common:attachments.noSearchResults", {
                  defaultValue: "No files match your search",
                })
                : t("common:attachments.noFilesYet", {
                  defaultValue: "No files uploaded yet",
                })}
            </Text>
          </Box>
        </Center>
      );
    }

    return (
      <Box>
        <SimpleGrid cols={{ base: 2, xs: 2, sm: 3, md: 4 }} spacing="md">
          {uploadedFiles.map((file) => {
            const isExcluded = file.id ? excludedIdsSet.has(file.id) : false;
            const isSelected = file.id ? selectedIds.has(file.id) : false;

            return (
              <SelectableFileCard
                key={file.id}
                file={file}
                selected={isSelected}
                disabled={isExcluded}
                onSelect={handleSelect}
                size="md"
                disabledTooltip={t("common:attachments.alreadyAttached")}
                fullWidth
              />
            );
          })}
        </SimpleGrid>

        {/* Load more button */}
        {hasMore && (
          <Center mt="md">
            <Button
              variant="subtle"
              onClick={handleLoadMore}
              loading={loadingMore}
              leftSection={<AppIcon iconName="ChevronDown" size={16} />}
            >
              {t("common:attachments.loadMore", { defaultValue: "Load more" })}
            </Button>
          </Center>
        )}
      </Box>
    );
  };

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={title || t("common:attachments.selectFiles")}
      position="right"
      size={size}
      closable={!isUploading && !isCreating}
      actionType="custom"
      customActions={<Group justify="space-between" w="100%">
        <Text size="sm" c="dimmed">
          {selectedIds.size > 0
            ? t("common:attachments.selectedCount", {
              count: selectedIds.size,
              defaultValue: `${selectedIds.size} selected`,
            })
            : t("common:attachments.noneSelected", {
              defaultValue: "No files selected",
            })}
        </Text>
        <Group gap="sm">
          <Button variant="subtle" onClick={onClose} size="lg">
            {t("common:actions.cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={selectedIds.size === 0}
            size="lg"
            leftSection={<AppIcon iconName="Check" size={16} />}
          >
            {t("common:attachments.confirmSelection", {
              defaultValue: "Attach Selected",
            })}
          </Button>
        </Group>
      </Group>}
    >
      <Stack gap="md" h="100%">
        {/* Upload section */}
        <Box>
          <Text size="sm" fw={500} mb="xs">
            {t("common:attachments.uploadNew", { defaultValue: "Upload New" })}
          </Text>
          <FileUpload
            accept={accept}
            maxSize={maxFileSize}
            onFilesSelected={handleFilesSelected}
            uploading={isUploading || isCreating}
            error={uploadError}
            success={uploadSuccess}
            size="sm"
            placeholder={t("common:attachments.dropOrClick", {
              defaultValue: "Drop file here or click to browse",
            })}
            description={t("common:attachments.uploadDescription", {
              defaultValue: "Upload a new file to attach",
            })}
          />
        </Box>

        <Divider
          label={t("common:attachments.orSelectExisting", {
            defaultValue: "Or select from existing files",
          })}
          labelPosition="center"
        />

        {/* Search and sort controls */}
        <Group gap="sm" align="flex-end">
          <TextInput
            placeholder={t("common:attachments.searchPlaceholder", {
              defaultValue: "Search files...",
            })}
            value={searchQuery}
            onChange={handleSearchChange}
            leftSection={<AppIcon iconName="Search" size={16} />}
            style={{ flex: 1 }}
            size="sm"
          />
          <SegmentedControl
            value={sortOption}
            onChange={handleSortChange}
            data={sortSegmentData}
            size="xs"
          />
        </Group>

        {/* File grid with scroll */}
        <ScrollArea style={{ flex: 1 }} offsetScrollbars>
          {renderFileGrid()}
        </ScrollArea>
      </Stack>
    </AppDrawer>
  );
};